"use client";

import { cn } from "@/lib/utils";
import DecorationLine from "../global/ui/DecorationLine";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useScroll,
  useSpring,
  useTransform,
  motion,
  MotionValue,
} from "framer-motion";
import { useIsMobile } from "@/hooks/useIsMobile";

function useStepReveal(
  progress: MotionValue<number>,
  at: number,
  firstVisible = false,
  stiff = 240,
  damp = 28,
) {
  const gate = useTransform(progress, (v) =>
    firstVisible ? 1 : v >= at ? 1 : 0,
  );

  //@ts-ignore
  const opacity = useSpring(gate, { stiffness: stiff, damping: damp });
  //@ts-ignore
  const yBase = useTransform(gate, [0, 1], [20, 0]);
  const y = useSpring(yBase, { stiffness: stiff, damping: damp });

  return { opacity, y };
}

export default function Timeline({ language }: { language: "en" | "de" }) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const [scrollRange, setScrollRange] = useState(0);
  const chip1Ref = useRef<HTMLDivElement | null>(null);
  const chip2Ref = useRef<HTMLDivElement | null>(null); // Step 02
  const chip3Ref = useRef<HTMLDivElement | null>(null); // Step 03
  const chip4Ref = useRef<HTMLDivElement | null>(null); // Step 04

  const [multiplier, setMultiplier] = useState(1.5);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;

      setMultiplier(w < 768 ? 0.15 : 1.5);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const recalc = useCallback(() => {
    const vw = viewportRef.current?.clientWidth ?? 0;
    const tw = trackRef.current?.scrollWidth ?? 0;
    const range = Math.max(0, tw - vw);
    setScrollRange(range);
  }, []);

  const scheduled = useRef(false);
  const scheduleRecalc = useCallback(() => {
    if (scheduled.current) return;
    scheduled.current = true;
    requestAnimationFrame(() => {
      recalc();
      scheduled.current = false;
    });
  }, [recalc]);

  useEffect(() => {
    if (!viewportRef.current || !trackRef.current) return;

    const ro = new ResizeObserver(() => scheduleRecalc());
    ro.observe(viewportRef.current);
    ro.observe(trackRef.current);

    const onResize = () => scheduleRecalc();
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [scheduleRecalc]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const xRaw = useTransform(scrollYProgress, [0, 1], [0, -scrollRange]);

  const xSpring = useSpring(xRaw, {
    stiffness: 200,
    damping: 25,
    mass: 0.9,
  });

  const xToday = useTransform(xSpring, (v) => -v * multiplier);

  const r01 = useStepReveal(scrollYProgress, 0.05);
  const r02 = useStepReveal(scrollYProgress, 0.15);
  const r03 = useStepReveal(scrollYProgress, 0.35);
  const r04 = useStepReveal(scrollYProgress, 0.65);

  const spacerHeight = `calc(${Math.max(1, 4)} * 50vh)`;

  return (
    <div
      style={{
        height: spacerHeight,
      }}
      ref={sectionRef}
      className="lg:max-w-[225rem] max-w-[95.5%] relative overflow-clip"
    >
      <div
        ref={viewportRef}
        className="flex flex-col gap-[3rem] items-center sticky top-[5rem]"
      >
        <div className="w-[min(187.5rem,100%)]">
          <NavigationBar />
        </div>

        <DecorationLine
          className="top-[6.5rem] left-0"
          orientation="horizontal"
        />

        <div ref={viewportRef} className="w-full relative">
          <motion.div
            ref={trackRef}
            className="absolute top-[-1.5rem] left-[2rem] z-[50]"
            style={{ x: xToday }}
          >
            <TodayFlag />
          </motion.div>

          <motion.div
            ref={trackRef}
            className="w-max relative z-10"
            style={{ x: xSpring }}
          >
            <Hours />
          </motion.div>
          <DecorationLine
            className="top-[6.5rem] left-0"
            orientation="horizontal"
          />

          <motion.div
            ref={trackRef}
            className="w-max relative z-[20]"
            style={{ x: xSpring }}
          >
            <div className="relative pt-[12rem] pl-[19rem] pr-[5rem] lg:pr-[10rem] flex  w-max  min-h-[calc(100vh-12vh)]">
              <motion.div ref={chip1Ref} style={r01} className="relative z-10">
                <svg
                  className="absolute z-0 w-[34.375rem] h-auto left-[4rem] top-[9rem]"
                  xmlns="http://www.w3.org/2000/svg"
                  width="276"
                  height="37"
                  fill="none"
                  viewBox="0 0 276 37"
                >
                  <path
                    stroke="#000"
                    d="M.5 0v24c0 6.627 5.373 12 12 12h263"
                    opacity="0.15"
                  ></path>
                </svg>
                <div className="flex flex-col gap-[1rem]">
                  <Chip
                    icon="✏️"
                    text={
                      language === "en"
                        ? "Register with our partner brokers"
                        : "Registriere dich bei unseren Partner-Broker"
                    }
                    enableStep={true}
                    step="Step 01"
                  />

                  <div className="flex items-center justify-end gap-[1rem] relative z-10 mt-[1.25rem]">

                    <div className="min-h-[7rem] flex items-center justify-center gap-[1rem] w-fit bg-white rounded-[1.5rem] px-[2rem]">
                      <span className="w-[1.125rem] h-[1.125rem] rounded-full bg-[#32CA0A]" />
                      <span className="text-black font-[500] text-[2.5rem]">
                        Exchange 1
                      </span>
                    </div>

                    <div className="min-h-[7rem] flex items-center justify-center gap-[1rem] w-fit bg-white rounded-[1.5rem] px-[2rem]">
                      <span className="w-[1.125rem] h-[1.125rem] rounded-full bg-[#FF8D28]" />
                      <span className="text-black font-[500] text-[2.5rem]">
                        Exchange 2
                      </span>
                    </div>

                    <div className="min-h-[7rem] flex items-center justify-center gap-[1rem] w-fit bg-white rounded-[1.5rem] px-[2rem]">
                      <span className="w-[1.125rem] h-[1.125rem] rounded-full bg-[#0B5BE0]" />
                      <span className="text-black font-[500] text-[2.5rem]">
                        Exchange 3
                      </span>
                    </div>

                  </div>

                </div>
              </motion.div>

              <motion.div
                ref={chip2Ref}
                style={r02}
                className="mt-[20rem] relative z-10"
              >
                <Chip
                  icon="✅"
                  text={
                    language === "en"
                      ? "Link your UID to Trademania."
                      : "Verknüpfe deine UID mit Trademania."
                  }
                  enableStep={true}
                  step="Step 02"
                  stepBgColor="bg-[#D9F9B9]"
                  stepTextColor="text-[#2a4f06]"
                />
              </motion.div>

              <motion.div
                ref={chip3Ref}
                style={r03}
                className="mt-[30rem] relative z-10"
              >
                <Chip
                  icon="📈"
                  text={
                    language === "en"
                      ? "Start trading. Your volume will be automatically counted towards the leaderboard."
                      : "Starte dein Trading. Dein Volumen wird automatisch fürs Leaderboard gezählt."
                  }
                  enableStep={true}
                  step="Step 03"
                  stepBgColor="bg-[#B9C0F9]"
                  stepTextColor="text-[#111855]"
                />
              </motion.div>

              <motion.div
                ref={chip4Ref}
                style={r04}
                className="mt-[40rem] relative z-10"
              >
                <Chip
                  icon="🎉"
                  text={
                    language === "en"
                      ? "Collect your rewards at the end of the month – it's that easy."
                      : "Kassiere deine Rewards am Monatsende – so einfach."
                  }
                  enableStep={true}
                  step="Step 04"
                  stepBgColor="bg-[#F9E0B9]"
                  stepTextColor="text-[#472F09]"
                />
              </motion.div>

              <div className="">
                <Background />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Chip({
  icon,
  text,
  enableStep,
  step,
  stepBgColor,
  stepTextColor,
  className,
}: {
  icon: React.ReactNode;
  text: string;
  enableStep: boolean;
  step?: string;
  stepBgColor?: string;
  stepTextColor?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "py-[1rem] relative z-10 pl-[2rem] lg:pl-[2.5rem] pr-[1rem] flex items-center gap-[1.75rem] lg:gap-[2rem] w-fit bg-white rounded-[1.5rem]",
        className,
      )}
    >
      <span className="block text-[2.5rem]">{icon}</span>
      <p
        className={`font-geist text-[2rem] lg:text-[2.5rem] font-[500] text-black mb-0 ${enableStep && "lg:pr-[2rem] pr-[1.5rem]"} `}
      >
        {text}
      </p>
      {enableStep && (
        <div
          className={`flex items-center justify-center min-h-[5.25rem] py-[0.5rem] px-[1rem] rounded-[0.625rem]  ${stepBgColor ?? "bg-[#E9B9F9]"}`}
        >
          <span
            className={`font-geist text-[2rem] lg:text-[2.5rem] ${stepTextColor ?? "text-[#520d69]"} font-[500]`}
          >
            {step}
          </span>
        </div>
      )}
    </div>
  );
}

function Hours() {
  const hours = Array.from({ length: 24 }, (_, i) => i + 1);
  const repeatedHours = [...hours, ...hours, ...hours, ...hours];
  return (
    <ul className="flex items-center w-max px-[2rem] opacity-[0.4]">
      {repeatedHours.map((h, index) => (
        <li
          className="w-[7rem] font-geist text-[2.25rem] font-[500]"
          key={index}
        >
          {h}
        </li>
      ))}
    </ul>
  );
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={cn(
        "aspect-square w-[3rem] h-[3rem] opacity-90 mix-blend-multiply",
        className,
      )}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <g opacity="0.9">
        <path
          fill="#fff"
          fillOpacity="0.01"
          d="M0 0h24v24H0z"
          style={{ mixBlendMode: "multiply" }}
        ></path>
        <path
          fill="#161616"
          d="M12 16.5 4.5 9l1.05-1.05L12 14.4l6.45-6.45L19.5 9z"
        ></path>
      </g>
    </svg>
  );
}

function NavigationBar() {
  return (
    <div className="flex flex-col gap-[6rem] relative lg:px-0 px-[2rem]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[0.25rem] opacity-[0.4]">
          <span className="font-geist text-black/90 text-[2.25rem] font-[500]">
            Timeline
          </span>

          <ChevronDown />
        </div>

        <div className="lg:flex hidden items-center gap-[3rem]">
          <span className="text-black/40 font-geist text-[2.25rem] font-[500] block">
            Properties
          </span>
          <span className="font-geist text-[2.25rem] font-[500] gap-[1rem] flex items-center">
            <span className="text-black/50">By</span>
            <span className="text-black/90">Timeline</span>
          </span>

          <span className="text-black/40 font-geist text-[2.25rem] font-[500] block">
            Filter
          </span>
          <span className="text-black/40 font-geist text-[2.25rem] font-[500] block">
            Sort
          </span>

          <span className="font-geist text-[2.25rem] font-[500] gap-[1rem] flex items-center">
            <svg
              className="w-[3rem] h-[3rem] aspect-square opacity-[0.4]"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <g opacity="0.4">
                <path
                  fill="#fff"
                  fillOpacity="0.01"
                  d="M0 0h24v24H0z"
                  style={{ mixBlendMode: "multiply" }}
                ></path>
                <path
                  fill="#161616"
                  d="m21.75 20.69-5.664-5.665a8.263 8.263 0 1 0-1.06 1.061l5.663 5.664zM3 9.75a6.75 6.75 0 1 1 6.75 6.75A6.76 6.76 0 0 1 3 9.75"
                ></path>
              </g>
            </svg>
            <span className="text-black/40">Search</span>
          </span>

          <div className="flex items-center gap-[0.62rem]">
            <span className="bg-black/40 w-[0.75rem] h-[0.75rem] rounded-full"></span>
            <span className="bg-black/40 w-[0.75rem] h-[0.75rem] rounded-full"></span>
            <span className="bg-black/40 w-[0.75rem] h-[0.75rem] rounded-full"></span>
          </div>

          <button className=" w-[11.375rem] h-[4.5rem] rounded-[1rem] border-[.25rem] border-[#3497c0] bg-primary flex items-center justify-center gap-[0.75rem] ">
            <span className="font-geist text-white text-[2.25rem] font-[500] pl-[1rem]">
              New
            </span>

            <div className="w-[.25rem] h-full bg-[#3497c0]"></div>
            <svg
              className="w-[3rem] h-[3rem] pr-[1rem]"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 20 20"
            >
              <g fill="#fff" opacity="0.9">
                <path
                  fillOpacity="0.01"
                  d="M0 0h20v20H0z"
                  style={{ mixBlendMode: "multiply" }}
                ></path>
                <path d="M10 13.75 3.75 7.5l.875-.875L10 12l5.375-5.375.875.875z"></path>
              </g>
            </svg>
          </button>
        </div>

        <div className="lg:hidden items-center flex gap-[1.5rem]">
          <svg
            className="w-[3rem] h-[3rem]"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <g opacity="0.4">
              <path
                fill="#fff"
                fillOpacity="0.01"
                d="M0 0h24v24H0z"
                style={{ mixBlendMode: "multiply" }}
              ></path>
              <path
                fill="#161616"
                d="m21.75 20.69-5.665-5.665a8.263 8.263 0 1 0-1.06 1.061l5.664 5.664zM3 9.75a6.75 6.75 0 1 1 6.75 6.75A6.76 6.76 0 0 1 3 9.75"
              ></path>
            </g>
          </svg>

          <svg
            className="w-[3rem] h-[3rem]"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <g opacity="0.4">
              <path
                fill="#fff"
                fillOpacity="0.01"
                d="M0 0h24v24H0z"
                style={{ mixBlendMode: "multiply" }}
              ></path>
              <path
                fill="#161616"
                d="m13.5 16.5 1.06-1.06 2.69 2.689V3h1.5v15.129l2.69-2.69L22.5 16.5 18 21zM12 13.5H1.5V15H12zM12 9H4.5v1.5H12zM12 4.5H7.5V6H12z"
              ></path>
            </g>
          </svg>

          <svg
            className="w-[3rem] h-[3rem]"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <g opacity="0.4">
              <path
                fill="#fff"
                fillOpacity="0.01"
                d="M0 0h24v24H0z"
                style={{ mixBlendMode: "multiply" }}
              ></path>
              <path
                fill="#161616"
                d="M22.5 6h-3.075c-.375-1.725-1.875-3-3.675-3s-3.3 1.275-3.675 3H1.5v1.5h10.575c.375 1.725 1.875 3 3.675 3s3.3-1.275 3.675-3H22.5zm-6.75 3c-1.275 0-2.25-.975-2.25-2.25s.975-2.25 2.25-2.25S18 5.475 18 6.75 17.025 9 15.75 9M1.5 18h3.075c.375 1.725 1.875 3 3.675 3s3.3-1.275 3.675-3H22.5v-1.5H11.925c-.375-1.725-1.875-3-3.675-3s-3.3 1.275-3.675 3H1.5zm6.75-3c1.275 0 2.25.975 2.25 2.25s-.975 2.25-2.25 2.25S6 18.525 6 17.25 6.975 15 8.25 15"
              ></path>
            </g>
          </svg>
          <button className=" w-[11.375rem] h-[4.5rem] rounded-[1rem] border-[.25rem] border-[#3497c0] bg-primary flex items-center justify-center gap-[0.75rem] ">
            <span className="font-geist text-white text-[2.25rem] font-[500] pl-[1rem]">
              New
            </span>

            <div className="w-[.25rem] h-full bg-[#3497c0]"></div>
            <svg
              className="w-[3rem] h-[3rem] pr-[1rem]"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 20 20"
            >
              <g fill="#fff" opacity="0.9">
                <path
                  fillOpacity="0.01"
                  d="M0 0h20v20H0z"
                  style={{ mixBlendMode: "multiply" }}
                ></path>
                <path d="M10 13.75 3.75 7.5l.875-.875L10 12l5.375-5.375.875.875z"></path>
              </g>
            </svg>
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[1rem]">
          <svg
            className="w-[4rem] h-[3rem]"
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="24"
            fill="none"
            viewBox="0 0 32 24"
          >
            <g opacity="0.4">
              <path
                fill="#fff"
                fillOpacity="0.01"
                d="M0 0h24v24H0z"
                style={{ mixBlendMode: "multiply" }}
                transform="rotate(-90 12 12)"
              ></path>
              <path
                fill="#161616"
                d="M16.5 12 9 19.5l-1.05-1.05L14.4 12 7.95 5.55 9 4.5z"
              ></path>
            </g>
            <g opacity="0.4">
              <path
                fill="#fff"
                fillOpacity="0.01"
                d="M0 0h24v24H0z"
                style={{ mixBlendMode: "multiply" }}
                transform="rotate(-90 16 8)"
              ></path>
              <path
                fill="#161616"
                d="M24.5 12 17 19.5l-1.05-1.05L22.4 12l-6.45-6.45L17 4.5z"
              ></path>
            </g>
          </svg>

          <span className="block font-geist text-black text-[2.25rem]">
            Hourly view
          </span>
        </div>

        <div className="flex items-center gap-[2rem]">
          <div className="flex items-center gap-[0.62rem]">
            <span className="block font-geist text-[2.25rem] text-black/40">
              Month
            </span>

            <ChevronDown className="opacity-[.4]" />
          </div>

          <div className="flex items-center gap-[0.62rem]">
            <ChevronDown className="rotate-90 opacity-[0.4]" />
            <span className="block font-geist text-[2.25rem] text-black/80">
              Today
            </span>

            <ChevronDown className="-rotate-90 opacity-[0.4]" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Background() {
  const isMobile = useIsMobile();
  return (
    <div className="absolute w-full h-[97%] bottom-0 left-0 z-0">
      <ul className="h-full flex items-start w-max relative gap-[29rem]">
        {Array.from({ length: isMobile ? 8 : 9 }).map((_, index) => {
          return (
            <li
              key={index}
              aria-hidden="true"
              className="w-[14rem] h-full bg-[#edeceb]"
            ></li>
          );
        })}
      </ul>
    </div>
  );
}

function TodayFlag() {
  return (
    <div className="flex flex-col items-center justify-center gap-[3rem]">
      <span className="font-geist py-[1rem] px-[1.5rem] bg-[#cd453c] text-white text-[2.25rem] w-fit rounded-full block font-[500]">
        Today
      </span>

      <div className="h-[138.875rem]  absolute z-10 top-[6.655rem] bg-[#cd543c] w-[0.125rem] flex flex-col items-center">
        <div className="w-[0.875rem] h-[0.875rem] rounded-full bg-[#cd543c] absolute top-[-0.4375rem]"></div>
      </div>
    </div>
  );
}
